import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
    Bell,
    Check,
    CheckCircle2,
    Copy,
    ExternalLink,
    RefreshCw,
    Sparkles,
    TrendingDown,
    TrendingUp,
    X,
    AlertTriangle,
    Clock,
    ShieldCheck,
    Mail,
} from 'lucide-react';
import mailboxAuditData from '../../data/mailbox_audit.json';
import { dashboardApi } from '../../services/dashboardApi';
import {
    fetchGeoOptimizerDashboard,
    applyOptimizerProposal,
    rejectOptimizerProposal,
    reopenOptimizerProposal,
    evaluateRolling72h,
    OptimizerProposal,
} from '../../services/geoOptimizerApi';

export interface Rolling72hSnapshot {
    evaluated_at?: string;
    window?: { from: string; to: string; hours: number; days?: number };
    campaign_id?: string;
    campaign_name?: string;
    zone?: 'green' | 'yellow' | 'red';
    zone_label?: string;
    current_daytime_budget_dkk?: number;
    recommended_daytime_budget_dkk?: number;
    spend_72h_dkk?: number;
    rev_72h_dkk?: number;
    purchases_72h?: number;
    instant_roas_72h?: number;
    projected_ltv_roas_72h?: number;
    salus_approved_72h?: number;
    cpa_salus_72h_dkk?: number;
    spend_today_dkk?: number;
    rev_today_dkk?: number;
    purchases_today?: number;
    instant_roas_today?: number;
    salus_approved_today?: number;
    cpa_salus_today_dkk?: number;
    today_salus_total?: number;
    today_salus_pending?: number;
    bayesian_priors?: {
        ltv_multiplier?: number;
        ltv_credibility_weight?: number;
        mu_v_dkk?: number;
        sigma_v_dkk?: number;
        payout_vif?: number;
        n_conversions_45d?: number;
        ev_salus_d0_dkk?: number;
        salus_regs_30d?: number;
        breakeven_instant_roas?: number;
        green_instant_roas?: number;
        red_instant_roas?: number;
        breakeven_salus_cpa_dkk?: number;
        green_salus_cpa_dkk?: number;
        target_spend_cohort_dkk?: number;
        post_change_min_spend_dkk?: number;
        ad_stop_loss_zero_salus_dkk?: number;
        ad_stop_loss_zero_purch_dkk?: number;
    };
    statistical_band?: {
        window_days?: number;
        window_hours?: number;
        target_spend_cohort_dkk?: number;
        cohort_completeness_pct?: number;
        expected_purchases_breakeven?: number;
        roas_1sd_pct?: number;
        roas_1sd_low?: number;
        roas_1sd_high?: number;
        roas_z_score?: number;
        lag_credit_dkk?: number;
        lag_adjusted_instant_roas?: number;
        lag_adjusted_ltv_roas?: number;
        last_budget_change_at?: string | null;
        hours_since_budget_change?: number | null;
        post_change_spend_dkk?: number;
        post_change_min_spend_dkk?: number;
        in_post_change_cooldown?: boolean;
    };
    ads_breakdown?: Array<{
        ad_id: string;
        ad_name: string;
        spend_72h_dkk: number;
        impressions_72h: number;
        frequency_72h: number;
        link_clicks_72h: number;
        link_ctr_pct: number;
        cpc_dkk: number;
        salus_approved_72h: number;
        cpa_salus_dkk: number;
        purchases_72h: number;
        revenue_72h_dkk: number;
        instant_roas_72h: number;
        needs_attention: boolean;
        fatigue_reasons: string[];
    }>;
}

const fmtDkk = (n?: number | null) =>
    `${Math.round(Number(n || 0)).toLocaleString('da-DK')} DKK`;

const fmtPct = (ratio?: number | null) =>
    `${(Number(ratio || 0) * 100).toFixed(1)}%`;

const SNOOZE_STORAGE_KEY = 'afima_se_72h_snoozed_v1';

export interface SnoozeEntry {
    until: number; // epoch ms
    label: string;
    snoozedAt: number;
}

export function loadSnoozeMap(): Record<string, SnoozeEntry> {
    try {
        const raw = localStorage.getItem(SNOOZE_STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw) as Record<string, SnoozeEntry>;
        const now = Date.now();
        const active: Record<string, SnoozeEntry> = {};
        for (const [k, v] of Object.entries(parsed)) {
            if (v && typeof v.until === 'number' && v.until > now) {
                active[k] = v;
            }
        }
        return active;
    } catch {
        return {};
    }
}

export function saveSnoozeEntry(id: number, hours: number, label: string): Record<string, SnoozeEntry> {
    const current = loadSnoozeMap();
    const now = Date.now();
    current[String(id)] = {
        until: now + hours * 3600 * 1000,
        label,
        snoozedAt: now,
    };
    try {
        localStorage.setItem(SNOOZE_STORAGE_KEY, JSON.stringify(current));
        window.dispatchEvent(new CustomEvent('afima-72h-snooze-changed'));
    } catch {
        // ignore storage errors
    }
    return current;
}

export function clearSnoozeEntry(id: number): Record<string, SnoozeEntry> {
    const current = loadSnoozeMap();
    delete current[String(id)];
    try {
        localStorage.setItem(SNOOZE_STORAGE_KEY, JSON.stringify(current));
        window.dispatchEvent(new CustomEvent('afima-72h-snooze-changed'));
    } catch {
        // ignore storage errors
    }
    return current;
}

export const NotificationCenter: React.FC = () => {
    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState<'pending' | 'vip_mail' | 'history'>('pending');
    const [dismissedMailIds, setDismissedMailIds] = useState<Record<string, boolean>>(() => {
        try {
            return JSON.parse(localStorage.getItem('afima_vip_mail_dismissed_v1') || '{}');
        } catch {
            return {};
        }
    });
    const [copiedId, setCopiedId] = useState<number | null>(null);
    const [snoozeMenuId, setSnoozeMenuId] = useState<number | null>(null);
    const [snoozeMap, setSnoozeMap] = useState<Record<string, SnoozeEntry>>(() => loadSnoozeMap());
    const [toastMsg, setToastMsg] = useState<string | null>(null);
    const panelRef = useRef<HTMLDivElement | null>(null);
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    useEffect(() => {
        const syncSnooze = () => setSnoozeMap(loadSnoozeMap());
        window.addEventListener('afima-72h-snooze-changed', syncSnooze);
        const timer = setInterval(syncSnooze, 30_000);
        return () => {
            window.removeEventListener('afima-72h-snooze-changed', syncSnooze);
            clearInterval(timer);
        };
    }, []);

    const { data, isFetching, refetch } = useQuery({
        queryKey: ['se-rolling-72h-notifications'],
        queryFn: () => fetchGeoOptimizerDashboard('se', 3, { includeFunnel: false }),
        refetchInterval: 60_000,
        staleTime: 30_000,
    });

    const { data: hygieneRaw } = useQuery({
        queryKey: ['debounce-credits-watchdog'],
        queryFn: async () => {
            const raw: any = await dashboardApi.getLeadHygiene(7);
            return raw?.body ? (typeof raw.body === 'string' ? JSON.parse(raw.body) : raw.body) : raw;
        },
        refetchInterval: 5 * 60_000,
        staleTime: 2 * 60_000,
    });

    const debounceCredits = hygieneRaw?.credits as
        | {
              balance: number | null;
              status: 'healthy' | 'warning' | 'critical' | 'unknown';
              warning_threshold: number;
              critical_threshold: number;
              is_low: boolean;
              avg_daily_burn_7d: number;
              estimated_days_remaining: number | null;
          }
        | undefined;

    const latestRun = data?.latest_run;
    const snap = (((data?.portfolio as any)?.rolling_72h) ||
        ((latestRun as any)?.portfolio_snapshot?.rolling_72h)) as Rolling72hSnapshot | undefined;
    const allProposals: OptimizerProposal[] = data?.proposals || [];
    const pendingProposals = allProposals.filter(
        (p) => p.status === 'pending' && !snoozeMap[String(p.id)]
    );
    const hasDbDebounceProposal = pendingProposals.some(
        (p) =>
            (p as any).proposal_type === 'debounce_credits_low' ||
            ((p.action_payload || {}) as any).notification_kind === 'debounce_credits_low'
    );
    const showLiveDebounceAlert =
        Boolean(debounceCredits?.is_low) &&
        !hasDbDebounceProposal &&
        !snoozeMap['999991'];
    const historyProposals = allProposals.filter(
        (p) => p.status === 'applied' || p.status === 'rejected' || Boolean(snoozeMap[String(p.id)])
    );
    const allVipMails: any[] = ((mailboxAuditData as any)?.vip_watchdog?.notifications || []);
    const activeVipMails = allVipMails.filter((m) => !m.is_read && !dismissedMailIds[m.id]);
    const totalPendingCount = pendingProposals.length + (showLiveDebounceAlert ? 1 : 0);
    const totalPendingAlerts = totalPendingCount + activeVipMails.length;

    const latestSeoProp = allProposals.find(
        (p) =>
            (p as any).platform === 'seo' ||
            String(((p.action_payload || {}) as any).notification_kind || '').startsWith('seo_')
    );
    const seoMetrics = ((latestSeoProp?.action_payload || {}) as any)?.metrics as
        | {
              SE?: { clicks_7d?: number; impr_7d?: number; scheduled?: number };
              NO?: { clicks_7d?: number; impr_7d?: number; scheduled?: number };
              DK?: { clicks_7d?: number; impr_7d?: number; scheduled?: number };
          }
        | undefined;
    const seoTotalClicks7d =
        (seoMetrics?.SE?.clicks_7d || 0) + (seoMetrics?.NO?.clicks_7d || 0) + (seoMetrics?.DK?.clicks_7d || 0);
    const seoTotalImpr7d =
        (seoMetrics?.SE?.impr_7d || 0) + (seoMetrics?.NO?.impr_7d || 0) + (seoMetrics?.DK?.impr_7d || 0);

    const handleDismissVipMail = (mailId: string, subject: string) => {
        const next = { ...dismissedMailIds, [mailId]: true };
        setDismissedMailIds(next);
        try {
            localStorage.setItem('afima_vip_mail_dismissed_v1', JSON.stringify(next));
        } catch {}
        showToast(`✓ Mail markeret som håndteret: "${subject.slice(0, 45)}"`);
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        if (open) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    const showToast = (msg: string, durationMs = 5500) => {
        setToastMsg(msg);
        setTimeout(() => setToastMsg(null), durationMs);
    };

    const refreshEvalMut = useMutation({
        mutationFn: () => evaluateRolling72h('se', false),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['se-rolling-72h-notifications'] });
            showToast('✓ 72-timers nøgletal og status er opdateret live!');
        },
        onError: () => {
            refetch();
            showToast('✓ Notifikationer opdateret.');
        },
    });

    const approveMut = useMutation({
        mutationFn: (proposalId: number) => applyOptimizerProposal(proposalId),
        onMutate: async (proposalId: number) => {
            await queryClient.cancelQueries({ queryKey: ['se-rolling-72h-notifications'] });
            const previous = queryClient.getQueryData(['se-rolling-72h-notifications']);
            queryClient.setQueryData(['se-rolling-72h-notifications'], (old: any) => {
                if (!old) return old;
                const targetProp = (old.proposals || []).find((x: any) => x.id === proposalId);
                const payload = (targetProp?.action_payload || {}) as Record<string, any>;
                const nextBudget =
                    payload.action === 'update_live_budget' && payload.daily_budget
                        ? Number(payload.daily_budget)
                        : undefined;
                return {
                    ...old,
                    portfolio: old.portfolio
                        ? {
                              ...old.portfolio,
                              current_spend_daily_dkk: nextBudget ?? old.portfolio.current_spend_daily_dkk,
                              rolling_72h: old.portfolio.rolling_72h
                                  ? {
                                        ...old.portfolio.rolling_72h,
                                        current_daytime_budget_dkk:
                                            nextBudget ?? old.portfolio.rolling_72h.current_daytime_budget_dkk,
                                    }
                                  : old.portfolio.rolling_72h,
                          }
                        : old.portfolio,
                    proposals: (old.proposals || []).map((x: any) =>
                        x.id === proposalId
                            ? { ...x, status: 'applied', decided_at: new Date().toISOString() }
                            : x
                    ),
                };
            });
            return { previous };
        },
        onSuccess: (res, proposalId) => {
            const p = allProposals.find((x) => x.id === proposalId);
            const payload = (p?.action_payload || {}) as Record<string, any>;
            const warnText = res?.warning || (res?.execution_result as any)?.warning;
            if (warnText) {
                showToast(`✓ ${warnText}`, 9000);
            } else if (payload.action === 'update_live_budget') {
                showToast(`✓ Budget opdateret til ${fmtDkk(payload.daily_budget)}/dag!`);
            } else if (payload.notification_kind === 'creative_sanitizing') {
                showToast(`✓ Markeret som gjort: ${payload.ad_name || p?.title}`);
            } else {
                showToast(`✓ Bekræftet og markeret som gennemført!`);
            }
            queryClient.invalidateQueries({ queryKey: ['se-rolling-72h-notifications'] });
        },
        onError: (err: any, _proposalId, ctx: any) => {
            if (ctx?.previous) {
                queryClient.setQueryData(['se-rolling-72h-notifications'], ctx.previous);
            }
            showToast(`⚠️ Fejl ved godkendelse: ${err?.message || 'Ukendt fejl'}`, 7000);
        },
    });

    const rejectMut = useMutation({
        mutationFn: ({ id }: { id: number; reason?: string }) => rejectOptimizerProposal(id),
        onMutate: async ({ id }) => {
            await queryClient.cancelQueries({ queryKey: ['se-rolling-72h-notifications'] });
            const previous = queryClient.getQueryData(['se-rolling-72h-notifications']);
            queryClient.setQueryData(['se-rolling-72h-notifications'], (old: any) => {
                if (!old) return old;
                return {
                    ...old,
                    proposals: (old.proposals || []).map((x: any) =>
                        x.id === id ? { ...x, status: 'rejected', decided_at: new Date().toISOString() } : x
                    ),
                };
            });
            return { previous };
        },
        onSuccess: () => {
            showToast('✓ Forslag afvist — nuværende indstilling fastholdes (se under "Historik / Gjort").');
            queryClient.invalidateQueries({ queryKey: ['se-rolling-72h-notifications'] });
        },
        onError: (err: any, _vars, ctx: any) => {
            if (ctx?.previous) {
                queryClient.setQueryData(['se-rolling-72h-notifications'], ctx.previous);
            }
            showToast(`⚠️ Kunne ikke afvise: ${err?.message || 'Ukendt fejl'}`);
        },
    });

    const reopenMut = useMutation({
        mutationFn: (id: number) => reopenOptimizerProposal(id),
        onMutate: async (id: number) => {
            const nextMap = clearSnoozeEntry(id);
            setSnoozeMap(nextMap);
            await queryClient.cancelQueries({ queryKey: ['se-rolling-72h-notifications'] });
            const previous = queryClient.getQueryData(['se-rolling-72h-notifications']);
            queryClient.setQueryData(['se-rolling-72h-notifications'], (old: any) => {
                if (!old) return old;
                return {
                    ...old,
                    proposals: (old.proposals || []).map((x: any) =>
                        x.id === id ? { ...x, status: 'pending', decided_at: null } : x
                    ),
                };
            });
            return { previous };
        },
        onSuccess: () => {
            setTab('pending');
            showToast('✓ Punkt taget op igen under "Afventer dig"');
            queryClient.invalidateQueries({ queryKey: ['se-rolling-72h-notifications'] });
        },
        onError: (_err, _id, ctx: any) => {
            if (ctx?.previous) {
                queryClient.setQueryData(['se-rolling-72h-notifications'], ctx.previous);
            }
        },
    });

    const handleSnooze = (p: OptimizerProposal, hours: number, label: string) => {
        const nextMap = saveSnoozeEntry(p.id, hours, label);
        setSnoozeMap(nextMap);
        setSnoozeMenuId(null);
        showToast(`⏰ Udskudt (${label}): "${p.title}"`);
    };

    const handleUnsnooze = (p: OptimizerProposal) => {
        const nextMap = clearSnoozeEntry(p.id);
        setSnoozeMap(nextMap);
        if (p.status !== 'pending') {
            reopenMut.mutate(p.id);
        } else {
            setTab('pending');
            showToast('✓ Punkt taget op igen under "Afventer dig"');
        }
    };

    const handleCopyPrompt = (p: OptimizerProposal) => {
        const payload = (p.action_payload || {}) as Record<string, any>;
        const text =
            payload.antigravity_prompt ||
            `Analyser venligst "${p.target_object_name}": ${p.rationale}`;
        navigator.clipboard.writeText(text);
        setCopiedId(p.id);
        showToast('📋 Analyse-prompt kopieret! Indsæt den i Antigravity-chatten.');
        setTimeout(() => setCopiedId(null), 3000);
    };

    const zone = snap?.zone || 'yellow';
    const zoneStyles =
        zone === 'green'
            ? {
                  badgeBg: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
                  headerBg: 'from-emerald-950/80 to-slate-900',
                  roasColor: 'text-emerald-400',
              }
            : zone === 'red'
            ? {
                  badgeBg: 'bg-rose-500/20 text-rose-300 border-rose-500/40',
                  headerBg: 'from-rose-950/80 to-slate-900',
                  roasColor: 'text-rose-400',
              }
            : {
                  badgeBg: 'bg-amber-500/20 text-amber-300 border-amber-500/40',
                  headerBg: 'from-amber-950/60 to-slate-900',
                  roasColor: 'text-amber-400',
              };

    return (
        <div className="relative" ref={panelRef}>
            {/* Bell Trigger Button */}
            <button
                onClick={() => setOpen((v) => !v)}
                className={`relative flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                    totalPendingAlerts > 0
                        ? 'bg-amber-500/10 border-amber-500/40 text-amber-200 hover:bg-amber-500/20'
                        : 'bg-slate-800/70 border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white'
                }`}
                title="72-Timers Rullende Styring & VIP Mail Notifikationer"
            >
                <div className="relative">
                    <Bell className="w-4 h-4" />
                    {totalPendingAlerts > 0 && (
                        <span className="absolute -top-1.5 -right-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-orange-500 px-1 text-[10px] font-bold text-white shadow">
                            {totalPendingAlerts}
                        </span>
                    )}
                </div>
                <span className="hidden sm:inline">
                    {pendingProposals.length > 0 && activeVipMails.length > 0
                        ? `${pendingProposals.length} afventer · ${activeVipMails.length} VIP Mail`
                        : pendingProposals.length > 0
                        ? `${pendingProposals.length} afventer godkendelse`
                        : activeVipMails.length > 0
                        ? `${activeVipMails.length} Vigtig Mail`
                        : 'Opgaver & Drift'}
                </span>
            </button>

            {/* Dropdown / Slide-over Drawer */}
            {open && (
                <div className="fixed sm:absolute right-2 sm:right-0 top-14 sm:top-11 w-[calc(100vw-1rem)] sm:w-[500px] max-h-[84vh] flex flex-col rounded-2xl bg-slate-900 border border-slate-700/90 shadow-2xl z-50 overflow-hidden">
                    {/* Top Header */}
                    <div className="p-4 bg-gradient-to-r from-slate-900 via-slate-900 to-slate-950 border-b border-slate-800">
                        <div className="flex items-start justify-between gap-2">
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-400">
                                        Afima · Opgave- & Driftscenter
                                    </span>
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800/80 text-slate-300 border border-slate-700">
                                        SEO · Ads · VIP Mail
                                    </span>
                                </div>
                                <h3 className="text-sm font-bold text-white mt-1">
                                    Overblik, Alarmer & Planlagte Rutiner
                                </h3>
                            </div>
                            <div className="flex items-center gap-1">
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        refreshEvalMut.mutate();
                                    }}
                                    disabled={isFetching || refreshEvalMut.isPending}
                                    className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800/70 transition-colors"
                                    title="Opdater nøgletal og notifikationer"
                                >
                                    <RefreshCw
                                        className={`w-4 h-4 ${
                                            isFetching || refreshEvalMut.isPending ? 'animate-spin text-orange-400' : ''
                                        }`}
                                    />
                                </button>
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setOpen(false);
                                    }}
                                    className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800/70 transition-colors"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        {/* Cross-Platform Executive Mini-KPIs (SEO, Meta Ads, Mail & Hygiene) */}
                        <div className="grid grid-cols-3 gap-2 mt-3">
                            <button
                                type="button"
                                onClick={() => {
                                    setOpen(false);
                                    navigate('/simplyloan-seo');
                                }}
                                className="text-left rounded-xl bg-slate-950/60 hover:bg-slate-950/90 border border-slate-800/90 hover:border-teal-500/40 p-2.5 transition-colors cursor-pointer"
                            >
                                <div className="text-[10px] text-slate-400">SimplyLoan SEO (7d)</div>
                                <div className="text-xs font-bold text-teal-400 mt-0.5">
                                    {seoTotalClicks7d} klik · {seoTotalImpr7d.toLocaleString('da-DK')} eksp.
                                </div>
                                <div className="text-[10px] text-slate-400">
                                    Kø: {seoMetrics?.SE?.scheduled ?? 14} SE · {seoMetrics?.NO?.scheduled ?? 13} NO ·{' '}
                                    {seoMetrics?.DK?.scheduled ?? 13} DK
                                </div>
                            </button>

                            <button
                                type="button"
                                onClick={() => {
                                    setOpen(false);
                                    navigate('/simplyloan/meta');
                                }}
                                className="text-left rounded-xl bg-slate-950/60 hover:bg-slate-950/90 border border-slate-800/90 hover:border-orange-500/40 p-2.5 transition-colors cursor-pointer"
                            >
                                <div className="text-[10px] text-slate-400">Meta Ads (72t SE)</div>
                                <div className={`text-xs font-bold mt-0.5 ${zoneStyles.roasColor}`}>
                                    {snap ? `${fmtPct(snap.instant_roas_72h)} ROAS` : 'Aktiv'}
                                </div>
                                <div className="text-[10px] text-slate-400">
                                    {snap
                                        ? `${snap.salus_approved_72h || 0} Salus · ~${fmtPct(snap.projected_ltv_roas_72h)} LTV`
                                        : '72t rullende styring'}
                                </div>
                            </button>

                            <button
                                type="button"
                                onClick={() => {
                                    setOpen(false);
                                    navigate(activeVipMails.length > 0 ? '/email/cleaner' : '/leads/hygiene');
                                }}
                                className="text-left rounded-xl bg-slate-950/60 hover:bg-slate-950/90 border border-slate-800/90 hover:border-indigo-500/40 p-2.5 transition-colors cursor-pointer"
                            >
                                <div className="text-[10px] text-slate-400">Mail & Lead Hygiene</div>
                                <div className="text-xs font-bold text-white mt-0.5">
                                    {activeVipMails.length} VIP mail{activeVipMails.length === 1 ? '' : 's'}
                                </div>
                                <div className="text-[10px] text-slate-400">
                                    {debounceCredits?.balance != null
                                        ? `DeBounce: ${debounceCredits.balance.toLocaleString('da-DK')} cr`
                                        : 'Hygiene aktiv'}
                                </div>
                            </button>
                        </div>
                    </div>

                    {/* Toast Feedback Banner */}
                    {toastMsg && (
                        <div className="px-4 py-2 bg-emerald-950/90 border-b border-emerald-700/50 text-emerald-200 text-xs font-medium flex items-center justify-between">
                            <span>{toastMsg}</span>
                            <button onClick={() => setToastMsg(null)} className="text-emerald-400 hover:text-white">
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    )}

                    {/* Tabs */}
                    <div className="flex border-b border-slate-800 bg-slate-950/40 px-4 pt-2 gap-4 text-xs">
                        <button
                            onClick={() => setTab('pending')}
                            className={`pb-2 font-semibold border-b-2 transition-colors flex items-center gap-1.5 ${
                                tab === 'pending'
                                    ? 'border-orange-500 text-white'
                                    : 'border-transparent text-slate-400 hover:text-slate-200'
                            }`}
                        >
                            <span>Afventer dig</span>
                            <span
                                className={`px-1.5 py-0.2 rounded-full text-[10px] ${
                                    totalPendingCount > 0
                                        ? 'bg-orange-500/20 text-orange-300 border border-orange-500/30'
                                        : 'bg-slate-800 text-slate-400'
                                }`}
                            >
                                {totalPendingCount}
                            </span>
                        </button>
                        <button
                            onClick={() => setTab('vip_mail')}
                            className={`pb-2 font-semibold border-b-2 transition-colors flex items-center gap-1.5 ${
                                tab === 'vip_mail'
                                    ? 'border-indigo-400 text-white'
                                    : 'border-transparent text-slate-400 hover:text-slate-200'
                            }`}
                        >
                            <Mail className="w-3.5 h-3.5 text-indigo-400" />
                            <span>Vigtig Mail (VIP)</span>
                            <span
                                className={`px-1.5 py-0.2 rounded-full text-[10px] ${
                                    activeVipMails.length > 0
                                        ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30'
                                        : 'bg-slate-800 text-slate-400'
                                }`}
                            >
                                {activeVipMails.length}
                            </span>
                        </button>
                        <button
                            onClick={() => setTab('history')}
                            className={`pb-2 font-semibold border-b-2 transition-colors flex items-center gap-1.5 ${
                                tab === 'history'
                                    ? 'border-orange-500 text-white'
                                    : 'border-transparent text-slate-400 hover:text-slate-200'
                            }`}
                        >
                            <span>Historik / Gjort</span>
                            <span className="px-1.5 py-0.2 rounded-full text-[10px] bg-slate-800 text-slate-400">
                                {historyProposals.length}
                            </span>
                        </button>
                    </div>

                    {/* Notification List */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-3">
                        {tab === 'pending' && totalPendingCount === 0 && (
                            <div className="text-center py-8 px-4 rounded-xl bg-slate-950/40 border border-slate-800/80">
                                <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2 opacity-90" />
                                <p className="text-sm font-semibold text-white">Alt er gennemgået og godkendt!</p>
                                <p className="text-xs text-slate-400 mt-1">
                                    Næste automatiske 72-timers evaluering kører kl. 17:00 CET (og sender dig en e-mail + notifikation her).
                                </p>
                            </div>
                        )}

                        {tab === 'pending' && showLiveDebounceAlert && debounceCredits && (
                            <div className="rounded-xl bg-amber-950/40 border border-amber-500/40 p-3.5 space-y-2.5">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex items-center gap-1.5">
                                        <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
                                        <span className="text-xs font-bold text-white leading-snug">
                                            DeBounce Credits Lav ({Number(debounceCredits.balance || 0).toLocaleString('da-DK')} tilbage)
                                        </span>
                                    </div>
                                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40 shrink-0">
                                        Email Hygiene
                                    </span>
                                </div>
                                <p className="text-xs text-slate-300 leading-relaxed">
                                    DeBounce saldoen er under grænsen på{' '}
                                    {Number(debounceCredits.warning_threshold || 25000).toLocaleString('da-DK')} credits (~
                                    {debounceCredits.estimated_days_remaining ?? '?'} dages runway ved nuværende forbrug på ~
                                    {Math.round(debounceCredits.avg_daily_burn_7d || 0)}/dag). Tank op så den natlige ESP-buffer rengøring ikke stopper.
                                </p>
                                <div className="flex flex-wrap items-center gap-2 pt-1">
                                    <a
                                        href="https://app.debounce.io/billing"
                                        target="_blank"
                                        rel="noreferrer"
                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-600 hover:bg-amber-500 text-white shadow transition-colors"
                                    >
                                        <span>Tank op på DeBounce.io</span>
                                        <ExternalLink className="w-3.5 h-3.5" />
                                    </a>
                                    <button
                                        onClick={() => {
                                            setOpen(false);
                                            navigate('/leads/hygiene');
                                        }}
                                        className="px-2.5 py-1.5 rounded-lg text-xs font-medium bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700"
                                    >
                                        Åbn Lead Hygiene
                                    </button>
                                    <button
                                        onClick={() => {
                                            const nextMap = saveSnoozeEntry(999991, 24, 'Til i morgen');
                                            setSnoozeMap(nextMap);
                                            showToast('⏰ DeBounce credit-advarsel udskudt i 24 timer.');
                                        }}
                                        className="px-2.5 py-1.5 rounded-lg text-xs font-medium bg-slate-800/80 hover:bg-slate-700 text-slate-400 border border-slate-700"
                                    >
                                        Udskyd 24t
                                    </button>
                                </div>
                            </div>
                        )}

                        {tab === 'pending' &&
                            pendingProposals.map((p) => {
                                const payload = (p.action_payload || {}) as Record<string, any>;
                                const isBudget = p.proposal_type === 'budget_shift';
                                const isDebounceLow =
                                    (p as any).proposal_type === 'debounce_credits_low' ||
                                    payload.notification_kind === 'debounce_credits_low';
                                const isSeo =
                                    payload.notification_kind === 'seo_weekly_deep_dive' ||
                                    payload.notification_kind === 'seo_daily_digest' ||
                                    (p as any).platform === 'seo';
                                const isCreative =
                                    p.proposal_type === 'ad_pause' || payload.notification_kind === 'creative_sanitizing';
                                const isLiveBudgetChange = payload.action === 'update_live_budget';
                                const pZone = payload.zone || zone;

                                return (
                                    <div
                                        key={p.id}
                                        className="rounded-xl bg-slate-950/80 border border-slate-800 p-3.5 space-y-2.5 hover:border-slate-700 transition-colors"
                                    >
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="flex items-center gap-1.5">
                                                {isDebounceLow ? (
                                                    <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
                                                ) : isSeo ? (
                                                    <TrendingUp className="w-4 h-4 text-teal-400 shrink-0" />
                                                ) : isBudget ? (
                                                    pZone === 'green' ? (
                                                        <TrendingUp className="w-4 h-4 text-emerald-400 shrink-0" />
                                                    ) : pZone === 'red' ? (
                                                        <TrendingDown className="w-4 h-4 text-rose-400 shrink-0" />
                                                    ) : (
                                                        <ShieldCheck className="w-4 h-4 text-amber-400 shrink-0" />
                                                    )
                                                ) : (
                                                    <Sparkles className="w-4 h-4 text-sky-400 shrink-0" />
                                                )}
                                                <span className="text-xs font-bold text-white leading-snug">
                                                    {p.title}
                                                </span>
                                            </div>
                                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-800 text-slate-300 border border-slate-700 shrink-0">
                                                {isDebounceLow
                                                    ? 'Email Hygiene'
                                                    : isSeo
                                                    ? payload.notification_kind === 'seo_weekly_deep_dive'
                                                        ? 'SEO Deep Dive (Mandag)'
                                                        : 'SEO Daglig Puls'
                                                    : isBudget
                                                    ? '72t Budget'
                                                    : 'Creative Hygiene'}
                                            </span>
                                        </div>

                                        <p className="text-xs text-slate-300 leading-relaxed">{p.rationale}</p>

                                        {/* Action Buttons */}
                                        <div className="flex flex-wrap items-center gap-2 pt-1">
                                            {isDebounceLow && (
                                                <>
                                                    <a
                                                        href={payload.topup_url || 'https://app.debounce.io/billing'}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-600 hover:bg-amber-500 text-white shadow transition-colors"
                                                    >
                                                        <span>Tank op på DeBounce.io</span>
                                                        <ExternalLink className="w-3.5 h-3.5" />
                                                    </a>
                                                    <button
                                                        onClick={() => {
                                                            setOpen(false);
                                                            navigate('/leads/hygiene');
                                                        }}
                                                        className="px-2.5 py-1.5 rounded-lg text-xs font-medium bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700"
                                                    >
                                                        Åbn Lead Hygiene
                                                    </button>
                                                    <button
                                                        onClick={() => approveMut.mutate(p.id)}
                                                        disabled={approveMut.isPending}
                                                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-emerald-600 hover:bg-emerald-500 text-white shadow transition-colors"
                                                    >
                                                        <Check className="w-3.5 h-3.5" />
                                                        <span>Marker som tanket op</span>
                                                    </button>
                                                </>
                                            )}

                                            {isBudget && isLiveBudgetChange && (
                                                <>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            approveMut.mutate(p.id);
                                                        }}
                                                        disabled={approveMut.isPending || rejectMut.isPending}
                                                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white shadow transition-all cursor-pointer disabled:opacity-60 ${
                                                            pZone === 'green'
                                                                ? 'bg-emerald-600 hover:bg-emerald-500'
                                                                : 'bg-rose-600 hover:bg-rose-500'
                                                        }`}
                                                    >
                                                        {approveMut.isPending && approveMut.variables === p.id ? (
                                                            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                                        ) : (
                                                            <Check className="w-3.5 h-3.5" />
                                                        )}
                                                        <span>
                                                            {approveMut.isPending && approveMut.variables === p.id
                                                                ? 'Gemmer...'
                                                                : pZone === 'green'
                                                                ? `Godkend opjustering (${fmtDkk(payload.daily_budget)}/dag)`
                                                                : `Godkend nedjustering (${fmtDkk(payload.daily_budget)}/dag)`}
                                                        </span>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            rejectMut.mutate({
                                                                id: p.id,
                                                                reason: 'Afvist i Notification Center — fastholder nuværende budget',
                                                            });
                                                        }}
                                                        disabled={rejectMut.isPending || approveMut.isPending}
                                                        className="px-2.5 py-1.5 rounded-lg text-xs font-medium bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 cursor-pointer disabled:opacity-60"
                                                    >
                                                        {rejectMut.isPending && rejectMut.variables?.id === p.id
                                                            ? 'Afviser...'
                                                            : `Afvis (Behold ${fmtDkk(payload.previous_daily_budget)})`}
                                                    </button>
                                                </>
                                            )}

                                            {isBudget && !isLiveBudgetChange && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        approveMut.mutate(p.id);
                                                    }}
                                                    disabled={approveMut.isPending}
                                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-600 hover:bg-amber-500 text-white shadow transition-all cursor-pointer disabled:opacity-60"
                                                >
                                                    {approveMut.isPending && approveMut.variables === p.id ? (
                                                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                                    ) : (
                                                        <Check className="w-3.5 h-3.5" />
                                                    )}
                                                    <span>Bekræft fastholdelse ({fmtDkk(payload.daily_budget)}/dag)</span>
                                                </button>
                                            )}

                                            {isCreative && (
                                                <>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleCopyPrompt(p);
                                                        }}
                                                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-sky-500/15 hover:bg-sky-500/25 text-sky-300 border border-sky-500/30 transition-colors cursor-pointer"
                                                    >
                                                        <Copy className="w-3.5 h-3.5" />
                                                        <span>
                                                            {copiedId === p.id
                                                                ? 'Kopieret til udklipsholder!'
                                                                : 'Kopiér prompt til Antigravity'}
                                                        </span>
                                                    </button>
                                                    {isSeo && (
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setOpen(false);
                                                                navigate('/simplyloan-seo');
                                                            }}
                                                            className="px-2.5 py-1.5 rounded-lg text-xs font-medium bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 cursor-pointer"
                                                        >
                                                            Åbn SimplyLoan SEO
                                                        </button>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            approveMut.mutate(p.id);
                                                        }}
                                                        disabled={approveMut.isPending}
                                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-600 hover:bg-emerald-500 text-white shadow transition-colors cursor-pointer disabled:opacity-60"
                                                    >
                                                        {approveMut.isPending && approveMut.variables === p.id ? (
                                                            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                                        ) : (
                                                            <Check className="w-3.5 h-3.5" />
                                                        )}
                                                        <span>Marker som gjort</span>
                                                    </button>
                                                </>
                                            )}

                                            {/* Udskyd (Snooze) Button & Quick Options */}
                                            {snoozeMenuId === p.id ? (
                                                <div
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="flex items-center gap-1.5 bg-slate-900 border border-slate-700 rounded-lg px-2 py-1"
                                                >
                                                    <span className="text-[11px] text-slate-400">Udskyd:</span>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleSnooze(p, 3, '3 timer');
                                                        }}
                                                        className="px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-[11px] font-medium text-amber-300 border border-slate-700 cursor-pointer"
                                                    >
                                                        3 timer
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleSnooze(p, 18, 'Til i morgen');
                                                        }}
                                                        className="px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-[11px] font-medium text-slate-200 border border-slate-700 cursor-pointer"
                                                    >
                                                        Til i morgen
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSnoozeMenuId(null);
                                                        }}
                                                        className="p-0.5 text-slate-400 hover:text-white cursor-pointer"
                                                        title="Annuller"
                                                    >
                                                        <X className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSnoozeMenuId(p.id);
                                                    }}
                                                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-slate-800/90 hover:bg-slate-700 text-slate-300 border border-slate-700 transition-colors cursor-pointer"
                                                    title="Udskyd dette punkt (f.eks. mens vi afventer bank-svar eller mere data)"
                                                >
                                                    <Clock className="w-3.5 h-3.5 text-amber-400" />
                                                    <span>Udskyd</span>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}

                        {tab === 'vip_mail' && (
                            <div className="space-y-2.5">
                                <div className="flex items-center justify-between px-1 text-[11px] text-slate-400">
                                    <span>Jesper (Talkontoret), Aktive Adtraction Kampagner & Skat</span>
                                    <button
                                        onClick={() => {
                                            setOpen(false);
                                            navigate('/email/cleaner');
                                        }}
                                        className="text-indigo-400 hover:text-indigo-300 font-semibold"
                                    >
                                        Åbn Mail Watchdog →
                                    </button>
                                </div>
                                {activeVipMails.length === 0 ? (
                                    <div className="text-center py-6 px-4 rounded-xl bg-slate-950/40 border border-slate-800/80 text-xs text-slate-400">
                                        Ingen ulæste VIP-mails fra Jesper eller aktive Adtraction-kampagner lige nu.
                                    </div>
                                ) : (
                                    activeVipMails.slice(0, 15).map((m) => (
                                        <div
                                            key={m.id}
                                            className="rounded-xl bg-slate-950/80 border border-slate-800 p-3 space-y-1.5 hover:border-indigo-500/40 transition-colors"
                                        >
                                            <div className="flex items-center justify-between gap-2">
                                                <span
                                                    className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                                                        m.vip_tag === 'JESPER_TALKONTORET'
                                                            ? 'bg-rose-500/20 text-rose-300 border border-rose-500/40'
                                                            : m.vip_tag === 'ACTIVE_CAMPAIGN'
                                                            ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/40'
                                                            : 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                                                    }`}
                                                >
                                                    {m.vip_reason}
                                                </span>
                                                <span className="text-[10px] text-slate-500">
                                                    {(m.received_at || '').slice(0, 10)}
                                                </span>
                                            </div>
                                            <div className="text-xs font-bold text-white leading-snug">
                                                {m.subject}
                                            </div>
                                            <div className="text-[11px] text-slate-400 font-mono truncate">
                                                Fra: {m.sender_name} &lt;{m.sender_address}&gt;
                                            </div>
                                            {m.preview && (
                                                <p className="text-[11px] text-slate-400 line-clamp-2">
                                                    {m.preview}
                                                </p>
                                            )}
                                            <div className="flex items-center justify-end gap-2 pt-1">
                                                <button
                                                    onClick={() => handleDismissVipMail(m.id, m.subject)}
                                                    className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-emerald-600/90 hover:bg-emerald-500 text-white"
                                                >
                                                    <Check className="w-3 h-3" />
                                                    <span>Markér håndteret</span>
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}

                        {tab === 'history' && historyProposals.length === 0 && (
                            <div className="text-center py-6 text-xs text-slate-400">
                                Ingen tidligere beslutninger registreret endnu.
                            </div>
                        )}

                        {tab === 'history' &&
                            historyProposals.map((p) => {
                                const snoozed = snoozeMap[String(p.id)];
                                return (
                                    <div
                                        key={p.id}
                                        className="rounded-xl bg-slate-950/50 border border-slate-800/80 p-3 space-y-1"
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="text-xs font-semibold text-slate-200">{p.title}</span>
                                            <span
                                                className={`text-[10px] px-2 py-0.5 rounded-full font-semibold shrink-0 ${
                                                    p.status === 'applied'
                                                        ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
                                                        : snoozed
                                                        ? 'bg-amber-500/15 text-amber-300 border border-amber-500/30'
                                                        : 'bg-slate-800 text-slate-400 border border-slate-700'
                                                }`}
                                            >
                                                {p.status === 'applied'
                                                    ? '✓ Gjort / Godkendt'
                                                    : snoozed
                                                    ? `⏰ Udskudt (${snoozed.label})`
                                                    : 'Udskudt / Afvist'}
                                            </span>
                                        </div>
                                        <p className="text-[11px] text-slate-400 line-clamp-2">{p.rationale}</p>
                                        <div className="flex items-center justify-between pt-0.5">
                                            <div className="text-[10px] text-slate-500 flex items-center gap-1">
                                                <Clock className="w-3 h-3" />
                                                <span>
                                                    {snoozed
                                                        ? `Udskudt til ${new Date(snoozed.until).toLocaleTimeString('da-DK', {
                                                              hour: '2-digit',
                                                              minute: '2-digit',
                                                          })}`
                                                        : p.decided_at
                                                        ? new Date(p.decided_at).toLocaleString('da-DK', {
                                                              day: '2-digit',
                                                              month: 'short',
                                                              hour: '2-digit',
                                                              minute: '2-digit',
                                                          })
                                                        : ''}
                                                </span>
                                            </div>
                                            {snoozed || p.status === 'rejected' || p.status === 'applied' ? (
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleUnsnooze(p);
                                                    }}
                                                    disabled={reopenMut.isPending}
                                                    className="text-[11px] font-medium text-amber-400 hover:text-amber-300 cursor-pointer"
                                                >
                                                    Tag op igen
                                                </button>
                                            ) : null}
                                        </div>
                                    </div>
                                );
                            })}
                    </div>

                    {/* Footer */}
                    <div className="p-3 bg-slate-950/90 border-t border-slate-800 flex flex-wrap items-center justify-between gap-2 text-xs">
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                onClick={() => {
                                    setOpen(false);
                                    navigate('/simplyloan-seo');
                                }}
                                className="text-teal-400 hover:text-teal-300 font-semibold cursor-pointer"
                            >
                                SimplyLoan SEO
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setOpen(false);
                                    navigate('/simplyloan/meta');
                                }}
                                className="text-slate-300 hover:text-white font-medium cursor-pointer"
                            >
                                Meta 72t ({fmtDkk(snap?.current_daytime_budget_dkk || 3200)})
                            </button>
                        </div>
                        <div className="flex items-center gap-2">
                            {debounceCredits?.balance != null && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setOpen(false);
                                        navigate('/leads/hygiene');
                                    }}
                                    className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold transition-colors cursor-pointer ${
                                        debounceCredits.is_low
                                            ? 'bg-amber-500/20 text-amber-300 border-amber-500/40 hover:bg-amber-500/30'
                                            : 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/25'
                                    }`}
                                    title={`DeBounce credits: ${debounceCredits.balance.toLocaleString('da-DK')} (~${debounceCredits.estimated_days_remaining ?? '?'} dages runway, alarm ved <25.000)`}
                                >
                                    DeBounce: {debounceCredits.balance.toLocaleString('da-DK')} cr
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => {
                                    setOpen(false);
                                    navigate('/email/cleaner');
                                }}
                                className="text-indigo-400 hover:text-indigo-300 font-medium cursor-pointer"
                            >
                                VIP Mail
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
